import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Divider } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { organizationFetch, organizationSelect, userAccessFetch } from 'appRedux/actions';
import { auth } from 'firebase/firebase';
import { actions } from 'packages/divisions';
import { MenuOption, MenuSelector } from 'packages/ui';
import {
  canGetOrganizationDocument,
  filterOrganizationsByAccess,
  isPackedClaims,
} from 'packages/utils';
import { clearPersistedViewState } from 'packages/utils/sessionStorage/clearPersistedViewState';
import IntlMessages from 'util/IntlMessages';

import MenuIcon from '../MenuIcon';
import NewOrganizationModal from '../NewOrganziationModal';
import useOrgOnboardingFilter from './hooks/useOrgOnboardingFilter';
import { sortOrganizationsByName } from './sortOrganizationsByName';
import styles from './styles.module.less';

const OrganizationSelector = ({ desktopTheme = false }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const hasMounted = useRef(false);
  const history = useHistory();
  const { orgId: paramOrgId } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);

  const selectedOrgId = useSelector(({ organizations }) => organizations.organization.id);
  const claims = useSelector(({ user }) => user.access.data?.claims);
  const claimOrgMap = useSelector(({ user }) => user.access.data?.claims?.org);
  const profileOrganizationsRaw = useSelector(({ user }) => user.profile.data?.organizations || {});
  // Only the ones in the claims; with a packed token (pk flag) the claims org map may be
  // incomplete, so the profile membership map becomes the source of truth.
  const userOrganizations = useSelector(({ user }) =>
    filterOrganizationsByAccess(user.access.data?.claims, user.profile.data?.organizations),
  );
  const { filteredOrganizations } = useOrgOnboardingFilter(userOrganizations);

  /**
   * Backend often writes users/{uid}.organizations before Auth custom claims propagate.
   * The combo filters by claims.org[id], so we re-fetch access a few times (forcing token
   * refresh) until profile and claims match.
   */
  const profileOrgIdsPendingClaims = useMemo(() => {
    // Null claims are the in-flight access fetch (the reducer clears data). Packed
    // tokens never grow a complete org map. Retrying either case refreshes the token,
    // which fetches access again and schedules another retry.
    if (!claims || isPackedClaims(claims)) {
      return '';
    }
    const claimSet = new Set(Object.keys(claimOrgMap || {}));
    return Object.values(profileOrganizationsRaw)
      .map(o => o?.id)
      .filter(Boolean)
      .filter(id => !claimSet.has(id))
      .sort()
      .join(',');
  }, [claims, claimOrgMap, profileOrganizationsRaw]);

  useEffect(() => {
    if (!profileOrgIdsPendingClaims) {
      return undefined;
    }
    const delaysMs = [250, 900, 2200, 4500];
    const timers = delaysMs.map(ms =>
      window.setTimeout(async () => {
        try {
          await auth.currentUser?.getIdToken(true);
        } catch {
          /* e.g. signed out */
        }
        dispatch(userAccessFetch());
      }, ms),
    );
    return () => timers.forEach(clearTimeout);
  }, [profileOrgIdsPendingClaims, dispatch]);

  const selectOrg = id => {
    dispatch(organizationSelect(id));
    if (canGetOrganizationDocument(claims, id)) {
      dispatch(organizationFetch(id));
    }
    dispatch(actions.assignedDivisionsFetch(id));
  };

  const onOrganizationChange = ({ currentTarget: { value } }) => {
    if (value === selectedOrgId) return;
    clearPersistedViewState();
    selectOrg(value);
  };

  const options = useMemo(
    () =>
      sortOrganizationsByName(filteredOrganizations, intl.locale).map(org => ({
        label: org.name,
        value: org.id,
      })),
    [filteredOrganizations, intl.locale],
  );

  const { pathname } = useSelector(({ common }) => common);

  const replaceOrgIdPath = newOrgId => {
    // Split the pathname /orgId?/-/rest/of/url to add the orgId if its null
    let divider = '/-';
    const [, rest = ''] = pathname.split(divider);
    const newPath = `/${newOrgId}${divider}${rest}`;
    history.replace(newPath);
  };

  useEffect(() => {
    // If the paramOrgId is missing then load one
    if (!paramOrgId && selectedOrgId) {
      replaceOrgIdPath(selectedOrgId);
    }
  }, []);

  // Dev note: this effect should be running before
  // AuthRoute effect has a chance to do the history.replace
  useEffect(() => {
    // If at the moment of the mount
    if (!hasMounted.current) {
      hasMounted.current = true;
      // and if paramOrgId and selected org id are different
      // then use the paramOrgId, and run a organizationSelect action
      if (!!paramOrgId && paramOrgId !== selectedOrgId) {
        // Only select the orgId if the user has access to it
        const userHasAccessToOrg = !!filteredOrganizations.find(org => org.id === paramOrgId);
        if (userHasAccessToOrg) {
          selectOrg(paramOrgId);
        }
      }
    }
  }, [selectedOrgId]);

  return (
    <div>
      <MenuSelector
        ariaLabel={intl.formatMessage({ id: 'config.division.organization' })}
        footer={
          <>
            <Divider className={styles.footerDivider} />
            <MenuOption
              as="button"
              className={styles.addOrganization}
              onClick={handleOpen}
              type="button"
            >
              <MenuIcon iconName="add" />
              <IntlMessages id="sidebar.nav.addOrganization" />
            </MenuOption>
          </>
        }
        className={classnames(styles.wrapper, desktopTheme && styles.desktopTheme)}
        onChange={onOrganizationChange}
        options={options}
        placeholder={<IntlMessages id="config.division.organization" />}
        value={selectedOrgId}
      />
      <NewOrganizationModal closeModal={handleClose} isOpen={isModalOpen} />
    </div>
  );
};

OrganizationSelector.propTypes = {
  desktopTheme: PropTypes.bool,
};

export default OrganizationSelector;
